import React, { useState, useEffect, useRef, useCallback } from 'react'
import { FaPaperclip, FaTimes, FaBullhorn } from 'react-icons/fa'
import { useParams } from 'react-router'
import { Box, Stack, Text, Heading, Textarea, Button, Loading, Badge, Icon, EmptyBox, EmptyCard, Link, ImageViewer, FileChooser, UserChip, SimpleBreadcrumbs, Tooltip, SimpleModal, FormControl, Alert } from '@/components/lib'
import api from '../../../api'
import utils, { ALLOWED_MESSAGE_FILE_TYPES } from '../../../utils/utils'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

export default function SiteMessages () {
  const { siteId } = useParams()
  const { trial, user } = useStore()
  useTitle('Site Messages', trial)

  // The site is loaded inside the board via the message endpoint (which gates
  // on can_read_site_messages) rather than `sites.get`, so a read-only
  // `site.readSiteMessages` user — who lacks `site.access` — can still open the
  // board. See list_for_site's `site` return value.
  if (!siteId) return <Loading />
  return (
    <SiteMessageBoard trial={trial} siteId={siteId} currentUser={user} />
  )
}

/**
 * The per-site message board. Cloned from ParticipantMessenger and re-scoped
 * from a 2-party admin/participant chat to a multi-reader site board:
 * bubbles align own-vs-others (your posts right, others left) so a site-only
 * viewer doesn't see admins' messages on their own side; `authorScope` drives
 * an "Admin" badge + subtle tint instead. Each message attributes its author
 * via UserChip. Keeps the 30s silent poll,
 * composer, attachment flow, link-parsing and scroll-to-bottom behaviour.
 */
export function SiteMessageBoard ({ trial, siteId, currentUser }) {
  const { markSiteBoardRead, addEmailSubscription, removeEmailSubscription } = useStore()
  const [site, setSite] = useState()
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [attachments, setAttachments] = useState([])
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [loadError, setLoadError] = useState()
  const messagesContainerRef = useRef(null)
  const trialId = trial?._id

  // Site message notifications are opt-IN (entry present = subscribed),
  // matching the participant-messenger convention. Users are NOT subscribed
  // by default; the button stores/removes the `site.<siteId>.messages` topic.
  const subscriptionTopic = `site.${siteId}.messages`
  const [subscribed, setSubscribed] = useState(
    currentUser?.subscriptions?.email?.includes(subscriptionTopic) ?? false
  )

  function updateSubscription (subscribe) {
    // subscribe=true  → add the topic   (user starts receiving emails)
    // subscribe=false → remove the topic (user stops receiving emails)
    const f = subscribe
      ? api.users.createEmailSubscription
      : api.users.deleteEmailSubscription
    f(currentUser._id, subscriptionTopic, subData => {
      const nowSubscribed = !!subData?.subscriptions?.email?.includes(subscriptionTopic)
      setSubscribed(nowSubscribed)
      // Keep the store user's subscriptions in sync so the toggle doesn't show
      // a stale state after navigating away and back.
      if (nowSubscribed) addEmailSubscription(subscriptionTopic)
      else removeEmailSubscription(subscriptionTopic)
      utils.toast('success', 'Successfully updated subscription')
    }, err => {
      utils.toast('error', err.message)
    })
  }

  // Permission checks need the loaded site doc (for the site._id scope).
  // Until `site` arrives from getMessages we render a loading state rather
  // than the `!canRead` block, which would otherwise fire prematurely.
  const canWrite = site && utils.canWriteSiteMessages(currentUser, trial, site)
  const canRead = site && utils.canReadSiteMessages(currentUser, trial, site)

  const scrollMessagesToBottom = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [])

  const getMessages = useCallback((silent = false) => {
    if (!siteId) return
    if (!silent) setLoadingMessages(true)
    // Server caps page_size at 100; request that so the board shows the 100
    // most recent messages (older history is unreachable in the UI for now
    // — see the plan's Phase 3 for pagination).
    api.sites.getMessages(siteId, 1, 100, ({ site: loadedSite, messages: newMessages }) => {
      if (!silent) setLoadingMessages(false)
      if (loadedSite) setSite(loadedSite)
      setMessages(newMessages)
      if (!silent) window.requestAnimationFrame(scrollMessagesToBottom)
    }, err => {
      // A 403 here means the user can't read this board at all — surface the
      // no-access card instead of spinning on <Loading /> forever.
      if (!silent) {
        setLoadingMessages(false)
        setLoadError(err)
      }
    })
  }, [siteId, scrollMessagesToBottom])

  useEffect(() => {
    getMessages()
  }, [getMessages])

  // 30s silent poll for new messages
  useEffect(() => {
    if (!siteId) return
    const interval = setInterval(() => getMessages(true), 30_000)
    return () => clearInterval(interval)
  }, [siteId, getMessages])

  // Mark the board read on open so unread badges clear. We do this on mount
  // rather than after the messages load because the API independently gates on
  // can_read_site_messages. Also zero this site's unread count in the store so
  // the navbar bubble / list badges update immediately without a refetch.
  useEffect(() => {
    if (!siteId) return
    api.sites.readMessages(siteId, () => markSiteBoardRead(siteId), () => {})
  }, [siteId, markSiteBoardRead])

  function createMessage () {
    if (!newMessage.trim() && !attachments.length) return
    setSendingMessage(true)
    const payload = { message: newMessage }
    if (attachments.length) payload.attachments = attachments.map(a => a.storedName)
    api.sites.createMessage(siteId, payload, message => {
      setSendingMessage(false)
      setMessages(prev => [...prev, message])
      setNewMessage('')
      setAttachments([])
      window.requestAnimationFrame(scrollMessagesToBottom)
    }, err => {
      utils.toast('error', 'Unable to send message', err.message)
      setSendingMessage(false)
    })
  }

  const parseMessageWithLinks = (message, isAdminSender = false) => {
    if (!message) return null
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = []
    let lastIndex = 0
    let match
    while ((match = urlRegex.exec(message)) !== null) {
      if (match.index > lastIndex) parts.push(message.substring(lastIndex, match.index))
      const url = match[0]
      parts.push(
        <Link
          key={match.index}
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          color={isAdminSender ? 'fg.onAccent' : 'primary.fg'}
          textDecoration='underline'
        >
          {url}
        </Link>
      )
      lastIndex = match.index + url.length
    }
    if (lastIndex < message.length) parts.push(message.substring(lastIndex))
    return parts.length ? parts : message
  }

  if (!site && loadError) {
    return (
      <Stack gap={5}>
        <SimpleBreadcrumbs items={[
          { label: 'Sites', to: `/trials/${trialId}/sites` }
        ]}
        />
        <EmptyCard icon='message' title='No access' description={loadError?.message || "You don't have permission to read this site's message board."} />
      </Stack>
    )
  }

  if (!site) return <Loading />

  if (!canRead) {
    return (
      <Stack gap={5}>
        <SimpleBreadcrumbs items={[
          { label: 'Sites', to: `/trials/${trialId}/sites` },
          { label: site?.name }
        ]}
        />
        <EmptyCard icon='message' title='No access' description="You don't have permission to read this site's message board." />
      </Stack>
    )
  }

  // chronological order (oldest first) for display
  const ordered = [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  return (
    <Stack gap={5}>
      <SimpleBreadcrumbs items={[
        { label: 'Sites', to: `/trials/${trialId}/sites` },
        { label: site?.name },
        { label: 'Messages' }
      ]}
      />

      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Box>
          <Heading as='h2' size='lg'>Messages at {site?.name}</Heading>
          <Text>A board for news and conversation between trial admins and this site's staff.</Text>
        </Box>
        <Stack direction='row'>
          {utils.canWriteTrialSiteMessages(currentUser, trial) &&
            <Button colorPalette='primary' variant='outline' onClick={() => setBroadcastOpen(true)}><Icon as={FaBullhorn} mr={1} /> Announce to all sites</Button>}
          {subscribed
            ? <Button colorPalette='primary' variant='ghost' onClick={() => updateSubscription(false)}>Unsubscribe</Button>
            : <Button colorPalette='primary' onClick={() => updateSubscription(true)}>Subscribe to updates</Button>}
        </Stack>
      </Box>

      <Stack gap={5} height={600} display='flex' flexDirection='column'>
        <Box
          ref={messagesContainerRef}
          flex={1}
          overflowY='auto'
          display='flex'
          flexDirection='column'
          gap={3}
          minHeight={0}
        >
          {!loadingMessages && !ordered.length && <EmptyBox icon='messages' title='Site messages' description='Messages will appear here once posted.' />}
          {loadingMessages && <Loading />}
          {ordered.map(m => {
            const isAdmin = m.authorScope === 'trial'
            const isOwn = m.author === currentUser?._id
            // Alignment is purely own-vs-others (your posts on the right,
            // everyone else's on the left) so a site-only viewer doesn't see
            // admins' messages mirrored onto their own side. `authorScope` /
            // `isAdmin` instead carry the origin distinction: an "Admin" badge
            // and, for incoming admin messages, a subtle primary tint so they
            // still read as "from the trial team" without claiming the right side.
            const alignEnd = isOwn
            const incomingAdmin = isAdmin && !isOwn
            return (
              <Box key={m._id} display='flex' justifyContent={alignEnd ? 'end' : 'start'} mb={3}>
                <Stack
                  width={{ md: '70%' }}
                  p={4}
                  borderRadius={18}
                  boxShadow={alignEnd ? 'md' : 'sm'}
                  position='relative'
                  transition='all 0.2s ease'
                  _hover={{ boxShadow: alignEnd ? 'lg' : 'md' }}
                  bg={alignEnd ? 'primary.solid' : (incomingAdmin ? 'primary.subtle' : 'bg.surface')}
                  {...(alignEnd
                    ? {
                        bgGradient: 'to-br',
                        gradientFrom: 'primary.solid',
                        gradientTo: 'primary.emphasized',
                        color: 'fg.onAccent'
                      }
                    : {
                        border: '1px solid',
                        borderColor: incomingAdmin ? 'primary.emphasized' : 'border.default'
                      })}
                  color={alignEnd ? 'fg.onAccent' : undefined}
                >
                  <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Box>
                      <UserChip
                        user={m.authorUser}
                        onlyAvatar
                        compact
                        avatarColorPalette={alignEnd ? 'whiteAlpha' : undefined}
                      />
                    </Box>
                    <Stack direction='row' alignItems='center' gap={1}>
                      <Text fontSize='xs' color={alignEnd ? 'fg.onAccent' : 'fg.muted'} fontWeight='medium'>
                        {utils.formatDateTime(m.createdAt)}
                      </Text>
                      {m.broadcastId && (
                        <Tooltip content='Broadcast to all sites'>
                          <Badge colorPalette={alignEnd ? 'whiteAlpha' : 'blue'} size='sm'><Icon as={FaBullhorn} mr={1} /> Broadcast</Badge>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  <Stack direction='row' alignItems='center' gap={2} mt={1}>
                    <Text fontSize='xs' fontWeight='bold' color={alignEnd ? 'fg.onAccent' : 'fg.muted'}>
                      {m.authorUser ? `${m.authorUser.firstName || ''} ${m.authorUser.lastName || ''}`.trim() || m.authorUser.email : 'Unknown'}
                    </Text>
                    {isAdmin && <Badge colorPalette={alignEnd ? 'whiteAlpha' : 'primary'} size='sm'>Admin</Badge>}
                  </Stack>

                  <Text
                    whiteSpace='pre-line'
                    color={alignEnd ? 'fg.onAccent' : 'fg.default'}
                    fontSize='sm'
                    lineHeight='1.5'
                    mt={1}
                  >
                    {parseMessageWithLinks(m.message, alignEnd)}
                  </Text>

                  {m.fileUrls?.length > 0 &&
                    <Stack gap={2} direction='row' flexWrap='wrap' mt={2}>
                      {m.fileUrls.map(f => {
                        const url = typeof f === 'string' ? f : f.url
                        const name = typeof f === 'string' ? url.split('/').pop() : f.name
                        const isImage = typeof f === 'object' ? f.contentType?.startsWith('image/') : true
                        if (isImage) return <ImageViewer key={url} url={url} withPreview />
                        return (
                          <Button size='xs' variant='outline' key={url} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                            <Icon as={FaPaperclip} mr={1} />{name}
                          </Button>
                        )
                      })}
                    </Stack>}
                </Stack>
              </Box>
            )
          })}
        </Box>

        {canWrite &&
          <Box p={3} bg='bg.surface' rounded='md' flexShrink={0}>
            <Textarea placeholder={`Write a new message to ${site?.name}...`} value={newMessage} onChange={e => setNewMessage(e.target.value)} />
            {attachments.length > 0 &&
              <Stack>
                <Stack direction='row' flexWrap='wrap' gap={2} mt={2}>
                  {attachments.map((a, i) => (
                    <Box key={i} display='flex' alignItems='center' gap={1} px={2} py={1} bg='bg.subtle' rounded='md' fontSize='xs'>
                      <Icon as={FaPaperclip} />
                      <Text fontSize='xs'>{a.name}</Text>
                      <Icon as={FaTimes} cursor='pointer' onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} />
                    </Box>
                  ))}
                </Stack>
                <Text fontSize='xs' color='fg.muted'>Attachments are stored with the site and visible to board readers.</Text>
              </Stack>}
            <Stack alignItems='end' mt={2}>
              <Stack direction='row' gap={2} alignItems='center'>
                <FileChooser
                  forType='site'
                  forObject={{ _id: siteId }}
                  accept={ALLOWED_MESSAGE_FILE_TYPES}
                  multiple
                  onComplete={file => setAttachments(prev => [...prev, { storedName: file.storedName, name: file.name }])}
                  onError={err => utils.toast('error', `Upload failed: ${err}`)}
                  trigger={<Button size='xs' variant='ghost'><Icon as={FaPaperclip} mr={1} /> Attach</Button>}
                />
                <Button onClick={createMessage} isLoading={sendingMessage} colorPalette='primary'>Send message</Button>
              </Stack>
            </Stack>
          </Box>}
      </Stack>

      <BroadcastModal
        isOpen={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        trial={trial}
      />
    </Stack>
  )
}

export function BroadcastModal ({ isOpen, onClose, trial, onSent }) {
  const [message, setMessage] = useState('')
  // Broadcast attachments are uploaded once against the *trial* (not a site),
  // so the single S3 object is shared by every fanned-out copy of the message.
  // The API prefixes the stored name with `trials/<trialId>/`, so FileChooser
  // must use forType='trial' for the key to match.
  const [attachments, setAttachments] = useState([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState()

  function send () {
    if (!message.trim() && !attachments.length) return
    setSending(true)
    setError()
    const payload = { message }
    if (attachments.length) payload.attachments = attachments.map(a => a.storedName)
    api.sites.broadcastMessage(trial._id, payload, () => {
      setSending(false)
      setMessage('')
      setAttachments([])
      onClose()
      utils.toast('success', 'Message broadcast to all sites')
      onSent && onSent()
    }, err => {
      setSending(false)
      setError(err?.message || 'Unable to broadcast')
    })
  }

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      size='2xl'
      header={<Stack><Heading size='md'><Icon as={FaBullhorn} mr={2} /> Announce to all sites</Heading></Stack>}
      body={
        <Stack gap={3}>
          <Alert status='info'>This posts the same message to every site's message board in this trial. Each site keeps its own thread for replies.</Alert>
          <FormControl label='Message'>
            <Textarea placeholder='Write a message for all sites...' value={message} onChange={e => setMessage(e.target.value)} rows={6} />
          </FormControl>
          {attachments.length > 0 &&
            <Stack>
              <Stack direction='row' flexWrap='wrap' gap={2}>
                {attachments.map((a, i) => (
                  <Box key={i} display='flex' alignItems='center' gap={1} px={2} py={1} bg='bg.subtle' rounded='md' fontSize='xs'>
                    <Icon as={FaPaperclip} />
                    <Text fontSize='xs'>{a.name}</Text>
                    <Icon as={FaTimes} cursor='pointer' onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} />
                  </Box>
                ))}
              </Stack>
              <Text fontSize='xs' color='fg.muted'>Attachments are posted to every site's board and visible to their board readers.</Text>
            </Stack>}
          <Box>
            <FileChooser
              forType='trial'
              forObject={{ _id: trial?._id }}
              accept={ALLOWED_MESSAGE_FILE_TYPES}
              multiple
              onComplete={file => setAttachments(prev => [...prev, { storedName: file.storedName, name: file.name }])}
              onError={err => utils.toast('error', `Upload failed: ${err}`)}
              trigger={<Button size='xs' variant='ghost'><Icon as={FaPaperclip} mr={1} /> Attach</Button>}
            />
          </Box>
          {error && <Alert status='error'>{error}</Alert>}
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end' gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button colorPalette='primary' onClick={send} isLoading={sending} isDisabled={!message.trim() && !attachments.length}>Broadcast</Button>
        </Stack>
      }
    />
  )
}
